/**
 * useActivityTracker Hook
 * Tracks user activity and manages token refresh and auto-logout
 */

import { useEffect, useRef, useCallback } from 'react';

interface ActivityTrackerOptions {
  onActivity?: () => void;
  onInactive?: () => void;
  inactivityTimeout?: number; // milliseconds (default: 15 minutes)
  activityCheckInterval?: number; // milliseconds (default: 1 minute)
}

const DEFAULT_INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes
const DEFAULT_ACTIVITY_CHECK_INTERVAL = 60 * 1000; // 1 minute

export const useActivityTracker = (options: ActivityTrackerOptions = {}) => {
  const {
    onActivity,
    onInactive,
    inactivityTimeout = DEFAULT_INACTIVITY_TIMEOUT,
    activityCheckInterval = DEFAULT_ACTIVITY_CHECK_INTERVAL,
  } = options;

  const lastActivityTimeRef = useRef<number>(Date.now());
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const activityCheckTimerRef = useRef<NodeJS.Timeout | null>(null);

  const updateActivity = useCallback(() => {
    lastActivityTimeRef.current = Date.now();
    if (onActivity) {
      onActivity();
    }
  }, [onActivity]);

  const checkInactivity = useCallback(() => {
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityTimeRef.current;

    if (timeSinceLastActivity >= inactivityTimeout) {
      // User has been inactive for the timeout period
      if (onInactive) {
        onInactive();
      }
    }
  }, [inactivityTimeout, onInactive]);

  useEffect(() => {
    // Track various user activities
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown',
    ];

    // Throttle activity updates to avoid excessive calls
    let throttleTimer: NodeJS.Timeout | null = null;
    const handleActivity = () => {
      if (throttleTimer) return;

      throttleTimer = setTimeout(() => {
        updateActivity();
        // Reset inactivity timer when activity is detected
        if (inactivityTimerRef.current) {
          clearTimeout(inactivityTimerRef.current);
        }
        inactivityTimerRef.current = setTimeout(() => {
          checkInactivity();
        }, inactivityTimeout);
        throttleTimer = null;
      }, 1000); // Update at most once per second
    };

    // Add event listeners
    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Set up inactivity check interval
    activityCheckTimerRef.current = setInterval(() => {
      checkInactivity();
    }, activityCheckInterval);

    // Set up initial inactivity timer
    inactivityTimerRef.current = setTimeout(() => {
      checkInactivity();
    }, inactivityTimeout);

    // Initial activity update
    updateActivity();

    // Cleanup
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });

      if (throttleTimer) {
        clearTimeout(throttleTimer);
      }

      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }

      if (activityCheckTimerRef.current) {
        clearInterval(activityCheckTimerRef.current);
      }
    };
  }, [updateActivity, checkInactivity, inactivityTimeout, activityCheckInterval]);

  return {
    updateActivity,
    getLastActivityTime: () => lastActivityTimeRef.current,
  };
};
